import { PencilSimple, Trash, Image } from "@phosphor-icons/react";
import { Tijdbalk, TijdbalkTicks } from "@/components/Tijdbalk";

const formatDuur = (min) => {
  if (!min) return "0u 0m";
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}u ${m}m`;
};

const tijd = (iso) => {
  if (!iso) return "--:--";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "--:--"
    : d.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" });
};

const KOLOMMEN = "grid-cols-[minmax(130px,1.2fr)_70px_70px_90px_90px_minmax(180px,2fr)_84px]";
const CEL = "px-2 border-l border-steel-200 first:border-l-0 min-w-0";

export const RittenBestuurderTabel = ({
  bestuurder, onEdit, onDeleteVraag, onDelete, confirmDel, onFotos,
}) => (
  <div data-testid={`rit-tabel-${bestuurder.naam}`}>
    {/* ── Desktop: kolommen met rasterlijnen + tijdbalk ── */}
    <div className="hidden lg:block">
      <div className={`grid ${KOLOMMEN} bg-steel-50 border-t border-steel-200 text-[10px] font-bold uppercase tracking-wider text-steel-400`}>
        <span className={`${CEL} py-1.5`}>Voertuig</span>
        <span className={`${CEL} py-1.5 text-center`}>Start</span>
        <span className={`${CEL} py-1.5 text-center`}>Einde</span>
        <span className={`${CEL} py-1.5 text-center`}>Duur</span>
        <span className={`${CEL} py-1.5 text-center`}>Foto's</span>
        <span className={`${CEL} py-1.5 flex items-center`}><TijdbalkTicks className="w-full" /></span>
        <span className={`${CEL} py-1.5 text-right`}>Acties</span>
      </div>

      {bestuurder.ritten.map((r) => {
        const aantalFotos = (r.fotos_start?.length || 0) + (r.fotos_eind?.length || 0);
        return (
          <div
            key={r.rid}
            className={`grid ${KOLOMMEN} border-t border-steel-200 items-center hover:bg-steel-50 transition-colors`}
            data-testid={`rit-rij-${r.rid}`}
          >
            <span className={`${CEL} py-2 text-sm font-semibold text-steel-900 truncate`}>
              {r.vehicle_kenteken}
            </span>
            <span className={`${CEL} py-2 text-xs font-mono text-center text-steel-600`}>{tijd(r.start_tijd)}</span>
            <span className={`${CEL} py-2 text-xs font-mono text-center text-steel-600`}>{tijd(r.eind_tijd)}</span>
            <span className={`${CEL} py-2 text-sm font-black font-mono tabular-nums text-center text-profit`}>
              {formatDuur(r.duur_minuten)}
            </span>
            <span className={`${CEL} py-2 text-center`}>
              {aantalFotos > 0 ? (
                <button
                  onClick={() => onFotos(r)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-sm"
                  data-testid={`view-photos-${r.rid}`}
                >
                  <Image size={11} /> {aantalFotos}
                </button>
              ) : (
                <span className="text-[11px] text-steel-300">–</span>
              )}
            </span>
            <span className={`${CEL} py-2`}>
              <Tijdbalk startIso={r.start_tijd} eindIso={r.eind_tijd} kleur="#0F172A" />
            </span>
            <span className={`${CEL} py-2 flex justify-end gap-1.5`}>
              <button
                onClick={() => onEdit(r)}
                title="Bewerken"
                className="h-8 w-8 flex items-center justify-center rounded-sm border border-steel-200 text-steel-500"
                data-testid={`edit-rit-${r.rid}`}
              >
                <PencilSimple size={14} />
              </button>
              {confirmDel === r.rid ? (
                <span className="flex gap-0.5 items-center">
                  <button onClick={() => onDelete(r.rid)} className="text-[10px] font-bold text-white bg-loss px-2 py-1 rounded-sm">Ja</button>
                  <button onClick={() => onDeleteVraag(null)} className="text-[10px] text-steel-500 bg-steel-100 px-2 py-1 rounded-sm">Nee</button>
                </span>
              ) : (
                <button
                  onClick={() => onDeleteVraag(r.rid)}
                  title="Verwijderen"
                  className="h-8 w-8 flex items-center justify-center rounded-sm border border-steel-200 text-loss"
                  data-testid={`del-rit-${r.rid}`}
                >
                  <Trash size={14} />
                </button>
              )}
            </span>
          </div>
        );
      })}
    </div>

    {/* ── Mobiel / tablet ── */}
    <div className="lg:hidden">
      {bestuurder.ritten.map((r) => {
        const aantalFotos = (r.fotos_start?.length || 0) + (r.fotos_eind?.length || 0);
        return (
          <div key={r.rid} className="px-3 py-2 border-t border-steel-100 flex items-start gap-2" data-testid={`rit-regel-${r.rid}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-steel-900 truncate">{r.vehicle_kenteken}</p>
              <p className="text-[11px] font-mono text-steel-500">
                {tijd(r.start_tijd)} → {tijd(r.eind_tijd)}
              </p>
              <div className="mt-1.5 max-w-[220px]">
                <Tijdbalk startIso={r.start_tijd} eindIso={r.eind_tijd} kleur="#0F172A" />
              </div>
              {aantalFotos > 0 && (
                <button
                  onClick={() => onFotos(r)}
                  className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-sm"
                  data-testid={`view-photos-m-${r.rid}`}
                >
                  <Image size={11} /> {aantalFotos} foto's
                </button>
              )}
            </div>
            <span className="text-sm font-black font-mono tabular-nums text-profit shrink-0 mt-0.5">
              {formatDuur(r.duur_minuten)}
            </span>
            <div className="flex gap-1.5 shrink-0">
              <button onClick={() => onEdit(r)} className="h-8 w-8 flex items-center justify-center rounded-sm border border-steel-200 text-steel-500" data-testid={`edit-rit-m-${r.rid}`}>
                <PencilSimple size={14} />
              </button>
              {confirmDel === r.rid ? (
                <div className="flex gap-0.5 items-center">
                  <button onClick={() => onDelete(r.rid)} className="text-[10px] font-bold text-white bg-loss px-2 py-1 rounded-sm">Ja</button>
                  <button onClick={() => onDeleteVraag(null)} className="text-[10px] text-steel-500 bg-steel-100 px-2 py-1 rounded-sm">Nee</button>
                </div>
              ) : (
                <button onClick={() => onDeleteVraag(r.rid)} className="h-8 w-8 flex items-center justify-center rounded-sm border border-steel-200 text-loss" data-testid={`del-rit-m-${r.rid}`}>
                  <Trash size={14} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>

    {/* ── Subtotaal per bestuurder ── */}
    <div
      className="flex items-center gap-3 px-3 py-2 border-t border-steel-200 bg-steel-50 text-[11px] font-bold"
      data-testid={`rit-bestuurder-totaal-${bestuurder.naam}`}
    >
      <span className="uppercase tracking-wider text-steel-900">Subtotaal {bestuurder.naam}</span>
      <span className="text-steel-400 font-semibold">
        {bestuurder.ritten.length} rit{bestuurder.ritten.length === 1 ? "" : "ten"}
        {bestuurder.voertuigen.length ? ` · ${bestuurder.voertuigen.join(", ")}` : ""}
      </span>
      <span className="ml-auto tabular-nums text-sm font-black font-mono">{formatDuur(bestuurder.minuten)}</span>
    </div>
  </div>
);
